/* Generated by restful-react */

import React from 'react'
import {
  Get,
  GetProps,
  useGet,
  UseGetProps,
  Mutate,
  MutateProps,
  useMutate,
  UseMutateProps,
} from 'restful-react'
import { IShipperRateSourceStatusEnum } from 'types/graphqlTypes'

export const SPEC_VERSION = 'v9'
export type Region = 'C' | 'MW' | 'N' | 'S' | 'SE' | 'W'

export interface StopResponse {
  postalCode?: string | null
  postalCode3?: string | null
  city?: string | null
  stateCode?: string | null
  isoCountryCode?: string | null
  region?: string | null
}

export interface ShipperRatesFilters {
  includes?: ('sourced_carrier_rates' | 'carrier_sales_rep' | 'shipper_lane')[]
  pickupPostalCode?: string
  pickupCity?: string
  pickupStateCode?: string
  pickupRegion?: Region[]
  pickupRadius?: number
  pickupSearchType?: string
  deliveryPostalCode?: string
  deliveryCity?: string
  deliveryStateCode?: string
  deliveryRegion?: Region[]
  deliveryRadius?: number
  deliverySearchType?: string
  transfixShipperLaneId?: number
  shipperRateId?: number
  shipperApplicationId?: number
  carrierApplicationId?: number
  activeStatus?: 'all' | 'active' | 'upcoming' | 'inactive'
  shipperLaneId?: string
  opsRepId?: number
  equipmentId?: string[]
  awardType?: 'primary' | 'backup' | 'published'
  carrierSalesRepId?: number
  assignedToMe?: boolean
  shouldSource?: boolean
  sourceStatus?: IShipperRateSourceStatusEnum[]
  dropType?: ('live' | 'drop')[]
}

export interface ShipperRatesResponse {
  activeStatusCounts?: {
    upcoming?: number
    active?: number
    inactive?: number
    all?: number
  }
  filterOptions?: { [key: string]: any }
  nextPage?: boolean
  shipmentPermissions?: {
    key?: string
    params?: { [key: string]: any }
    permissions?: { [key: string]: any }
  }[]
  shipperRates?: ShipperRateObject[]
  totalCount?: number
  userPermissions?: { [key: string]: any }
}

export interface RateObject {
  amount?: string
  createdAt?: string
  createdById?: number
  id?: number
  rateTypeKey?: string
  updatedAt?: string
}

export type UserObject = {
  id?: number
  parentUserId?: number
  firstName?: string
  lastName?: string
} | null

export interface ShipperRateObject {
  id?: number
  shipperLaneId?: string | null
  rfpId?: number | null
  rfpLaneId?: number | null
  rfpName?: string | null
  rate?: RateObject
  fuelRate?: RateObject
  minChargeRate?: RateObject
  volume?: number | null
  volumeType?: string | null
  volumeConversions?: {
    weeklyVolume?: number | null
  }
  pricingType?: string
  shipperMiles?: number | null
  mapboxMiles?: number | null
  datMiles?: number | null
  pickup?: StopResponse
  delivery?: StopResponse
  startDate?: string
  endDate?: string | null
  awardType?: string
  isPickupDropTrailer?: boolean | null
  isDeliveryDropTrailer?: boolean | null
  isActive?: boolean
  isRepricedRate?: boolean
  previousRates?: ShipperRateObject[]
  equipment?: {
    id?: number
    createdAt?: string
    updatedAt?: string
    key?: string
    name?: string
    parentId?: number
    position?: number
  }
  shouldSource?: boolean | null
  sourceStatus?: IShipperRateSourceStatusEnum
  carrierSalesRep?: UserObject
  shipperApplication?: {
    id?: number
    name?: string
    opsRep?: UserObject
  }
  sourcedCarrierRates?: SourcedCarrierRates[] | null
  sourcedCarrierRateCount?: number
  googleSheetsUrl?: string | null
  salesforceRfpId?: string | null
  pricingProgram?: {
    id?: number
    createdAt?: string
    updatedAt?: string
    program?: string
    description?: string
  }
  rfpFeedback?: {
    tttAtAward?: number | null
    tfxhAllIn?: string | null
  }
  carrierTargetRates?: {
    transfixCostPrediction40thPercentile?: number | null
  }
}

/**
 * Sourced Rates on Shipper Rates
 */
export interface SourcedCarrierRates {
  canDoWeekends?: boolean
  carrierApplication?: {
    id?: number
    name?: string
  }
  confirmed?: boolean
  createdAt?: string
  createdBy?: {
    id?: number
    parentUserId?: number
    fullName?: string
  }
  id?: number
  equipment?: {
    id?: string
    key?: string
    name?: string
    parentId?: string
    position?: string
  }
  maxCapacity?: number | null
  minCapacity?: number | null
  rate?: RateObject
  updatedAt?: string | null
  updatedBy?: {
    id?: string
    parentUserId?: number
    fullName?: string
  } | null
}

/**
 * Shipper rate attributes to Update
 */
export interface ShipperRateUpdate {
  carrierSalesRepId?: number | null
  shouldSource?: boolean
  sourceStatus?: IShipperRateSourceStatusEnum
}

export interface GetShipperRatesQueryParams {
  /**
   * Filters
   */
  filters?: { [key: string]: any }
  /**
   * Page Number
   */
  pageCount?: number
  /**
   * Results per Page
   */
  perPage?: number
  /**
   * Sort Order
   */
  sortOrder?: string
  /**
   * Field to Sort on
   */
  sortField?: string
}

export type GetShipperRatesProps = Omit<
  GetProps<ShipperRatesResponse, unknown, GetShipperRatesQueryParams, void>,
  'path'
>

/**
 * Get all shipper rates
 */
export const GetShipperRates = (props: GetShipperRatesProps) => (
  <Get<ShipperRatesResponse, unknown, GetShipperRatesQueryParams, void>
    path='/v9/shipper_rates'
    {...props}
  />
)

export type UseGetShipperRatesProps = Omit<
  UseGetProps<ShipperRatesResponse, unknown, GetShipperRatesQueryParams, void>,
  'path'
>

/**
 * Get all shipper rates
 */
export const useGetShipperRates = (props: UseGetShipperRatesProps) =>
  useGet<ShipperRatesResponse, unknown, GetShipperRatesQueryParams, void>(
    `/v9/shipper_rates`,
    props
  )

export interface GetShipperRateQueryParams {
  /**
   * Includes
   */
  includes?: string
}

export interface GetShipperRatePathParams {
  /**
   * Shipper Rate ID
   */
  shipperRateId: number
}

export type GetShipperRateProps = Omit<
  GetProps<ShipperRateObject, unknown, GetShipperRateQueryParams, GetShipperRatePathParams>,
  'path'
> &
  GetShipperRatePathParams

/**
 * Get a shipper rate
 */
export const GetShipperRate = ({ shipperRateId, ...props }: GetShipperRateProps) => (
  <Get<ShipperRateObject, unknown, GetShipperRateQueryParams, GetShipperRatePathParams>
    path={`/v9/shipper_rates/${shipperRateId}`}
    {...props}
  />
)

export type UseGetShipperRateProps = Omit<
  UseGetProps<ShipperRateObject, unknown, GetShipperRateQueryParams, GetShipperRatePathParams>,
  'path'
> &
  GetShipperRatePathParams

/**
 * Get a shipper rate
 */
export const useGetShipperRate = ({ shipperRateId, ...props }: UseGetShipperRateProps) =>
  useGet<ShipperRateObject, unknown, GetShipperRateQueryParams, GetShipperRatePathParams>(
    (paramsInPath: GetShipperRatePathParams) => `/v9/shipper_rates/${paramsInPath.shipperRateId}`,
    { pathParams: { shipperRateId }, ...props }
  )

export interface UpdateShipperRatePathParams {
  /**
   * Shipper Rate ID
   */
  shipperRateId: number
}

export type UpdateShipperRateProps = Omit<
  MutateProps<void, unknown, void, ShipperRateUpdate, UpdateShipperRatePathParams>,
  'path' | 'verb'
> &
  UpdateShipperRatePathParams

/**
 * Update shipper rate
 */
export const UpdateShipperRate = ({ shipperRateId, ...props }: UpdateShipperRateProps) => (
  <Mutate<void, unknown, void, ShipperRateUpdate, UpdateShipperRatePathParams>
    verb='PUT'
    path={`/v9/shipper_rates/${shipperRateId}`}
    {...props}
  />
)

export type UseUpdateShipperRateProps = Omit<
  UseMutateProps<void, unknown, void, ShipperRateUpdate, UpdateShipperRatePathParams>,
  'path' | 'verb'
> &
  UpdateShipperRatePathParams

/**
 * Update shipper rate
 */
export const useUpdateShipperRate = ({ shipperRateId, ...props }: UseUpdateShipperRateProps) =>
  useMutate<void, unknown, void, ShipperRateUpdate, UpdateShipperRatePathParams>(
    'PUT',
    (paramsInPath: UpdateShipperRatePathParams) =>
      `/v9/shipper_rates/${paramsInPath.shipperRateId}`,
    { pathParams: { shipperRateId }, ...props }
  )
